import {
  GridColDef,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid-premium'

/**
 * Read a boolean that may have come back as a string.
 *
 * The service writes every column with pandas' `dtype=str`, so a `false`
 * posted from here is stored and returned as the STRING `'False'` — and a
 * non-empty string is truthy in JavaScript, so an unticked box springs back to
 * ticked the next time it is read. Mirrors the service's own `_is_truthy`.
 *
 * Note that `finishBeforeHard` and `requiresSterilisation` below are declared
 * WITHOUT this and have the same bug latent; they are left alone here rather
 * than fixed in passing, because changing what an existing tick means is a
 * separate change from adding a new column.
 */
const asBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

export const TAB_DIRECTORIES_MAPPING = {
  'production-requirements': 'production-requirements',
  'workstation-downtime': 'workstation-downtime',
  // Product master data and production requirements are two views over the
  // same InputProductRequirementsDelta table; the master-data slug returns
  // the week-invariant columns, the production-requirements slug returns the
  // per-week rows.
  'product-master-data': 'product-master-data',
  'packing-routes': 'packing-routes',
  'cleaning-mapping': 'cleaning-mapping',
  'resource-and-material-constraints': 'resource-and-material-constraints',
  'resource-and-input-mapping': 'resource-and-input-mapping',
  'resource-and-output-mapping': 'resource-and-output-mapping',
  'product-clean-changeover': 'product-clean-changeover',
  'base-material-clean-changeover': 'base-material-clean-changeover',
  // Basket & steriliser SORs (Phase 2). 1:1 with SteriliserTypesDelta /
  // LineSteriliserRoutesDelta. BasketFamiliesDelta is projected from
  // resource-and-material-constraints rows where ConstraintType='basket_pool'.
  // ProductSterilisationDelta is projected from product-master-data extension
  // columns. See docs/BASKET_STERILISER_PHASE2_PORTAL_SPEC.md.
  'steriliser-types': 'steriliser-types',
  'steriliser-routing': 'steriliser-routing',
  // Plant properties rather than week data — which lines share one set of
  // equipment, and what moving a line between bottle formats costs. Held
  // here so a tenant configures them once instead of on every schedule.
  'resource-switching': 'resource-switching',
  'format-changeover': 'format-changeover',
  // The tenant's standard shift pattern — the one week the plant normally
  // runs. Seeded into every week of a new schedule; a planner then overrides
  // only the weeks that differ.
  'resource-capacity-profile': 'resource-capacity-profile',
  // Holding tanks. The vessel and its product eligibility describe the plant,
  // so both are week-invariant master data and belong here beside
  // packing-routes. `tank-week-state` deliberately does NOT: a week's opening
  // stock is last week's measured output, and one master figure copied into
  // eight weeks would assert eight starting stocks from one typed number.
  // See docs/SOR_INPUT_TABLE_ALIGNMENT.md — §4 phase 3 proposes a
  // NOT_MASTER_DATA set to make that exclusion explicit rather than implied by
  // absence, but it is not built, so `tank-week-state` is currently absent
  // from this map for the same unstated reason `trials` is.
  //
  // A tank is only worth declaring if a route fills or drains it, which is
  // why the six nullable tank columns are on the packing-routes SOR grid too
  // — vessels without routes are inert (the engine reports TANK_UNUSED).
  tanks: 'tanks',
  'tank-eligibility': 'tank-eligibility',
  // Kitchen cycles: the order a kitchen runs a set of base materials in,
  // week after week. A statement about the kitchen, so master data.
  'kitchen-cycles': 'kitchen-cycles',
}

/** Per-slug factory for an "Add Row" button default. Returns `undefined`
 *  when manual row addition isn't supported for the slug — most existing
 *  tabs are populated from upstream data, so they don't expose Add Row.
 *
 *  `nextNewRowId` is a negative integer (so it can't collide with the
 *  positive ids the server assigns); use it to seed predicate-key columns
 *  with a placeholder unique value the user can then edit.
 */
export const SOR_DEFAULT_NEW_ROW: Partial<
  Record<
    keyof typeof TAB_DIRECTORIES_MAPPING,
    (nextNewRowId: number) => Record<string, unknown>
  >
> = {
  'steriliser-types': (nextNewRowId) => ({
    id: nextNewRowId,
    steriliserTypeId: `NEW_TYPE_${Math.abs(nextNewRowId)}`,
    basketsPerCycle: 0,
    cycleHours: 0,
    instanceCount: 1,
  }),
  'steriliser-routing': (nextNewRowId) => ({
    id: nextNewRowId,
    steriliserTypeId: '',
    basketFamilyId: `NEW_${Math.abs(nextNewRowId)}`,
    lines: '',
  }),
  // Both tank tables are authored by hand — there is no upstream source for a
  // vessel — so both need Add Row. The placeholder tankId is unique per new
  // row because it is the merge predicate: two blank keys would merge into
  // one row on save.
  tanks: (nextNewRowId) => ({
    id: nextNewRowId,
    tankId: `NEW_TANK_${Math.abs(nextNewRowId)}`,
    capacity: null,
    UOM: null,
    maxHoldHours: null,
    // Explicitly true rather than null: on is the default, this row is posted
    // as typed, and a null here would reach the grid as an indeterminate
    // checkbox.
    allowAccumulation: true,
  }),
  'tank-eligibility': (nextNewRowId) => ({
    id: nextNewRowId,
    tankId: `NEW_TANK_${Math.abs(nextNewRowId)}`,
    productCode: '',
  }),
}

const positiveIntCellProps = (params: GridPreProcessEditCellProps) => {
  const val = params.props.value
  const invalid =
    val !== null &&
    val !== undefined &&
    val !== '' &&
    (!Number.isInteger(Number(val)) || Number(val) <= 0)
  return { ...params.props, error: invalid }
}

// Read `ConstraintType` from the live edit (otherFieldsProps) if the user
// is editing it simultaneously, otherwise fall back to the saved row.
// Mirrors `getRequiresSterilisation` — used to gate basket-pool-only fields.
// Exported so the per-schedule resource-and-material-constraints table
// (`SchedulerResourceAndMaterialConstraints.muiTable.tsx`) can reuse the
// same gating logic.
export const getConstraintType = (
  params: GridPreProcessEditCellProps & {
    row?: { ConstraintType?: unknown }
  }
): string => {
  const live = (
    params as GridPreProcessEditCellProps & {
      otherFieldsProps?: { ConstraintType?: { value?: unknown } }
    }
  ).otherFieldsProps?.ConstraintType?.value
  if (live !== undefined) return String(live ?? '')
  return String(params.row?.ConstraintType ?? '')
}

// Validator for basket-pool-only columns: empty is always fine; non-empty is
// rejected on non-basket_pool rows and must be a non-negative integer on
// basket_pool rows. Exported for the per-schedule constraints table.
export const basketPoolNonNegativeIntCellProps = (
  params: GridPreProcessEditCellProps
) => {
  const ct = getConstraintType(params)
  const val = params.props.value
  const empty = val === null || val === undefined || val === ''
  if (empty) return { ...params.props, error: false }
  if (ct !== 'basket_pool') return { ...params.props, error: true }
  const invalid = !Number.isInteger(Number(val)) || Number(val) < 0
  return { ...params.props, error: invalid }
}

// Coerce a possibly-null/empty/string field to a finite number. Used by the
// production-requirements recompute formula (`still = quantity - made`) so
// nulls and pre-typed string values don't poison the math.
const toNumber = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// Read `requiresSterilisation` from the live edit (otherFieldsProps) if the
// user is editing it simultaneously, otherwise fall back to the saved row.
const getRequiresSterilisation = (
  params: GridPreProcessEditCellProps & {
    row?: { requiresSterilisation?: unknown }
  }
): boolean => {
  const live = (
    params as GridPreProcessEditCellProps & {
      otherFieldsProps?: { requiresSterilisation?: { value?: unknown } }
    }
  ).otherFieldsProps?.requiresSterilisation?.value
  if (live !== undefined) return !!live
  return !!params.row?.requiresSterilisation
}
export const SORColumns = {
  'packing-routes': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'baseProductCode',
      headerName: 'Input Product Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'baseProductName',
      headerName: 'Input Product Description',
      type: 'string',
      flex: 1,
    },
    {
      field: 'kitchen',
      headerName: 'Kitchen',
      flex: 1,
      editable: true,
      type: 'string',
      description:
        'A kitchen, or a declared tank: a tank here means this segment DRAWS from it (Tank → Lines), and Kitchen Run Rate is its drain rate.',
    },
    {
      field: 'kitchenRunRate',
      headerName: 'Kitchen Run Rate',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      // A product GROUP code, stored under `productCode` so existing routes
      // keep their key. A product with no group is its own group.
      field: 'productCode',
      headerName: 'Product Group code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'productName',
      headerName: 'Product Name',
      type: 'string',
      flex: 1,
    },
    {
      field: 'lineRunRate1',
      headerName: 'Line Run Rate 1',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'lineRunRate2',
      headerName: 'Line Run Rate 2',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'lineRunRate3',
      headerName: 'Line Run Rate 3',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'lineRunRate4',
      headerName: 'Line Run Rate 4',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'packingLine1',
      headerName: 'Line Group 1',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'packingLine2',
      headerName: 'Line Group 2',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'packingLine3',
      headerName: 'Line Group 3',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'packingLine4',
      headerName: 'Line Group 4',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'pr1',
      headerName: 'PR Factor 1',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'pr2',
      headerName: 'PR Factor 2',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'pr3',
      headerName: 'PR Factor 3',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'pr4',
      headerName: 'PR Factor 4',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'prepTime',
      headerName: 'Off-set',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'inputPrepTime',
      headerName: 'Prep Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'minRunTime',
      headerName: 'Min Run Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      // A preference, not a floor: a value below Min Run Time is clamped up
      // to it by the engine, since the floor already guarantees more than the
      // preference asks. Present on the per-schedule grid since the run-length
      // work; it belongs here too — it is a property of the route.
      field: 'preferredRunTime',
      headerName: 'Preferred Run Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'maxRunTime',
      headerName: 'Max Run Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'maxBatches',
      headerName: 'Max Batches',
      flex: 1,
      editable: true,
      type: 'number',
    },
    // ── Holding tanks: a route row is a SEGMENT ──────────────────────────
    // Mirrors the nullable tank columns on the per-schedule grid
    // (`SchedulerPackagingRoutes.muiTable.tsx`). The tank itself is named in
    // the Kitchen or a Line column (engine v7.152.0), so only the route, the
    // segment and the hold need columns. Leave them blank and the row is an
    // ordinary Kitchen -> Lines route. The legacy inputTankId / outputTankId /
    // inputProductCode columns are gone from here; master data still holding
    // them is moved by SchedulerService/scripts/convert_tank_route_columns.py
    // --master, and the engine reads them meanwhile.
    //
    // They belong in the SOR because the chain topology is a plant fact, and
    // because without them the CSV template this tab downloads cannot express
    // a tank route at all — `dataConfig` filters the export to the declared
    // columns, so an omitted column is invisible rather than merely absent.
    {
      field: 'routeId',
      headerName: 'Route',
      type: 'string',
      flex: 1,
      editable: true,
      description:
        'Groups the segments of one chain, one row per step. Needed for Tank → Kitchen2 → Lines, entered as three rows on one route: Kitchen → Tank (fill), Tank → Kitchen2 (transfer), Kitchen2 → Lines. Blank on an ordinary route.',
    },
    {
      field: 'segmentIndex',
      headerName: 'Segment',
      type: 'number',
      flex: 1,
      editable: true,
      description: 'Position in that chain: 1, 2, …',
    },
    {
      field: 'maturationHours',
      headerName: 'Maturation (h)',
      type: 'number',
      flex: 1,
      editable: true,
      description:
        'How long the batch must be held in the tank before it may be drained, in HOURS. Sits on the segment that FILLS the tank.',
    },
  ],
  'production-requirements': (
    data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      headerName: 'Product code',
      field: 'productCode',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Product description',
      field: 'productName',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      type: 'number',
      flex: 1,
      editable: true,
      // Recompute Quantity still to make whenever Quantity changes.
      valueSetter: (value, row) => {
        const q = toNumber(value)
        const made = toNumber(row.quantityMade)
        return {
          ...row,
          quantity: value as number | string,
          quantityStillToMake: Math.max(0, q - made),
        }
      },
    },
    {
      headerName: 'Quantity made',
      field: 'quantityMade',
      type: 'number',
      flex: 1,
      editable: true,
      valueGetter: (_value, row) => toNumber(row.quantityMade),
      valueSetter: (value, row) => {
        const made = toNumber(value)
        const q = toNumber(row.quantity)
        return {
          ...row,
          quantityMade: made,
          quantityStillToMake: Math.max(0, q - made),
        }
      },
    },
    {
      headerName: 'Quantity still to make',
      field: 'quantityStillToMake',
      type: 'number',
      flex: 1,
      editable: true,
      valueGetter: (_value, row) => {
        if (
          row.quantityStillToMake !== null &&
          row.quantityStillToMake !== undefined &&
          row.quantityStillToMake !== ''
        ) {
          return toNumber(row.quantityStillToMake)
        }
        return Math.max(0, toNumber(row.quantity) - toNumber(row.quantityMade))
      },
      valueSetter: (value, row) => ({
        ...row,
        quantityStillToMake: toNumber(value),
      }),
    },
    {
      headerName: "Don't Start Before",
      field: 'dontStartBefore',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Finish Before',
      field: 'finishBefore',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Finish Before (Hard)',
      field: 'finishBeforeHard',
      type: 'boolean',
      flex: 1,
      editable: true,
      description:
        'When ticked, the Finish Before time is enforced as a hard constraint instead of a soft late penalty.',
    },
  ],
  'workstation-downtime': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      headerName: 'Resource code',
      field: 'resource',
      flex: 1,
      type: 'string',
    },
    {
      // Blank is stored as blank and shown as the resource's own code: each
      // resource is its own group until one is typed (v7.180.0).
      headerName: 'Resource group code',
      field: 'resourceGroupCode',
      type: 'string',
      flex: 1,
      editable: true,
      valueGetter: (value: unknown, row: Record<string, unknown>) =>
        value != null && String(value).trim() !== ''
          ? value
          : row?.resource ?? '',
    },
    {
      headerName: 'Resource Type',
      field: 'resourceType',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Down Time',
      field: 'downTime',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Site Location',
      field: 'site_location',
      flex: 1,
      type: 'string',
    },
  ],
  'product-master-data': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      headerName: 'Product code',
      field: 'productCode',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Product description',
      field: 'productName',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Input Product code',
      field: 'inputProductCode',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Input Product description',
      field: 'inputProductName',
      type: 'string',
      flex: 1,
    },
    {
      headerName: 'Conversion Factor',
      field: 'conversionFactor',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Required Temperature',
      field: 'requiredTemperature',
      type: 'singleSelect',
      flex: 1,
      editable: true,
      valueOptions: [132, 135],
    },
    {
      headerName: 'Usage Group',
      field: 'usageGroup',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const val = params.props.value
        const invalid =
          val !== null &&
          val !== undefined &&
          val !== '' &&
          !Number.isInteger(Number(val))
        return { ...params.props, error: invalid }
      },
      valueSetter: (value, row) => ({
        ...row,
        usageGroup:
          value != null && value !== ''
            ? Number.parseInt(String(value), 10)
            : null,
      }),
    },
    {
      headerName: 'Requires Sterilisation',
      field: 'requiresSterilisation',
      type: 'boolean',
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Basket Family',
      field: 'basketFamilyId',
      type: 'string',
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const requires = getRequiresSterilisation(params)
        const val = params.props.value
        const missing =
          requires && (val === null || val === undefined || val === '')
        return { ...params.props, error: missing }
      },
    },
    {
      // The product's bottle format, and the source of truth for the format
      // vocabulary — the Format Changeover tab prices transitions between the
      // values used here. Free text and nullable; a blank never incurs a
      // format changeover. See Scheduler/docs/FORMAT_CHANGEOVER.md.
      headerName: 'Format',
      field: 'format',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      // Blank is stored as blank and shown as the product's own code: each
      // product is its own group until one is typed.
      headerName: 'Product Group',
      field: 'productGroup',
      type: 'string',
      flex: 1,
      editable: true,
      valueGetter: (value: unknown, row: Record<string, unknown>) =>
        value != null && String(value).trim() !== ''
          ? value
          : row?.productCode ?? '',
    },
  ],
  'cleaning-mapping': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'asset',
      headerName: 'Resource Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'programName',
      headerName: 'Program Name',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'cip',
      headerName: 'CIP',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'cipChannel',
      headerName: 'CIP Channel',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'prepTime',
      headerName: 'Prep Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'preRinseTime',
      headerName: 'Pre Rinse Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'causticTime',
      headerName: 'Caustic Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'finalRinseTime',
      headerName: 'Final Rinse Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'drainTime',
      headerName: 'Drain Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'totalTargetTime',
      headerName: 'Total Target Time',
      flex: 1,
      editable: true,
      type: 'number',
    },
  ],
  'resource-and-material-constraints': (
    data: Record<string, unknown>[]
  ): GridColDef[] => {
    // Discover existing ConstraintType values from the loaded data so legacy
    // free-text values continue to render correctly. `basket_pool` is always
    // offered (rows with this type are projected to BasketFamiliesDelta).
    const observed = new Set<string>()
    data.forEach((row) => {
      const v = row.ConstraintType
      if (typeof v === 'string' && v.length > 0) observed.add(v)
    })
    const valueOptions = Array.from(
      new Set(['material', 'resource', 'basket_pool', ...observed])
    )
    return [
      {
        field: 'ResourceConstraint',
        headerName: 'Resource Constraint Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'ConstraintType',
        headerName: 'Constraint Type',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        valueOptions,
      },
      {
        field: 'ConstraintAmount',
        headerName: 'Constraint Amount',
        type: 'number',
        flex: 1,
        editable: true,
        preProcessEditCellProps: positiveIntCellProps,
      },
      {
        field: 'UOM',
        headerName: 'Unit Of Measure',
        type: 'string',
        flex: 1,
        editable: true,
      },
      // Basket-pool-only: user-entered hour-0 state for the basket family.
      // Persisted to BasketFamiliesDelta via _write_constraints_with_basket_pool.
      // See Scheduler/docs/BASKET_TRACKING_HOURLY_LOGIC.md.
      {
        field: 'openingFreeBaskets',
        headerName: 'Opening Free Baskets',
        type: 'number',
        flex: 1,
        editable: true,
        preProcessEditCellProps: basketPoolNonNegativeIntCellProps,
      },
      {
        field: 'fullBaskets',
        headerName: 'Full Baskets',
        type: 'number',
        flex: 1,
        editable: true,
        preProcessEditCellProps: basketPoolNonNegativeIntCellProps,
      },
    ]
  },
  'resource-and-input-mapping': (
    data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      field: 'inputProductCode',
      headerName: 'Input Product Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'inputProductName',
      headerName: 'Input Product Description',
      type: 'string',
      flex: 1,
    },
    {
      field: 'resource',
      headerName: 'Resource Constraint Name',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'runRate',
      headerName: 'Run Rate',
      flex: 1,
      editable: true,
      type: 'number',
    },
    {
      field: 'workStationCode',
      headerName: 'Work Station Code',
      type: 'string',
      flex: 1,
    },
  ],
  'resource-and-output-mapping': (
    data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      field: 'productCode',
      headerName: 'Product Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'productName',
      headerName: 'Product Description',
      type: 'string',
      flex: 1,
    },
    {
      field: 'resource',
      headerName: 'Resource Constraint Name',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'runRate',
      headerName: 'Run Rate',
      editable: true,
      flex: 1,
      type: 'number',
    },
    {
      field: 'workStationCode',
      headerName: 'Work Station Code',
      type: 'string',
      flex: 1,
    },
  ],
  'product-clean-changeover': (
    data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      field: 'line_code',
      headerName: 'Line Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'previous_product_code',
      headerName: 'Previous Product Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'next_product_code',
      headerName: 'Next Product Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'remove_clean',
      headerName: 'Remove Clean',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'remove_offset',
      headerName: 'Remove Offset',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'other_changeover',
      headerName: 'Other Changeover',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_cost',
      headerName: 'Changeover Cost',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_environmental_cost',
      headerName: 'Changeover Environmental Cost',
      type: 'number',
      flex: 1,
      editable: true,
    },
  ],
  'base-material-clean-changeover': (
    data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      field: 'kitchen_code',
      headerName: 'Kitchen Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'previous_material_code',
      headerName: 'Previous Material Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'next_base_material_code',
      headerName: 'Next Base Material Code',
      type: 'string',
      flex: 1,
    },
    {
      field: 'remove_clean',
      headerName: 'Remove Clean',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'other_changeover',
      headerName: 'Other Changeover',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_cost',
      headerName: 'Changeover Cost',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_environmental_cost',
      headerName: 'Changeover Environmental Cost',
      type: 'number',
      flex: 1,
      editable: true,
    },
  ],
  'steriliser-types': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'steriliserTypeId',
      headerName: 'Steriliser Type',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'basketsPerCycle',
      headerName: 'Baskets / Cycle',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: positiveIntCellProps,
    },
    {
      field: 'cycleHours',
      headerName: 'Cycle Hours',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: positiveIntCellProps,
    },
    {
      field: 'instanceCount',
      headerName: 'Instances',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: positiveIntCellProps,
    },
  ],
  // NOTE: the active routing UI is `SORSterilizerRouting.view.tsx`, which
  // uses a chip + multi-select Autocomplete for `lines` sourced from
  // `availableLines`. These plain-text columns are a fallback for the
  // generic SORView path — keep them in sync with the wire shape so the
  // table stays usable if anything ever routes through the generic view.
  'steriliser-routing': (data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'steriliserTypeId',
      headerName: 'Steriliser Type',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'basketFamilyId',
      headerName: 'Basket Family',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'lines',
      headerName: 'Lines',
      type: 'string',
      flex: 2,
      editable: true,
    },
  ],

  // Equipment switching between lines that share one physical set of
  // equipment. A row IS the directed pair, so both halves key it — which is
  // why neither is editable here: editing a pair in place would create a
  // duplicate rather than move the row (same rule as the per-schedule grid).
  'resource-switching': (_data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'previous_resource',
      headerName: 'From Resource',
      type: 'string',
      flex: 1,
      editable: false,
    },
    {
      field: 'next_resource',
      headerName: 'To Resource',
      type: 'string',
      flex: 1,
      editable: false,
    },
    {
      field: 'switching_time',
      headerName: 'Switching Time (hrs)',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      // Naming a resource lets the switch CONSUME it while it runs, which
      // is how reconfiguring one line stops its partner producing.
      field: 'resource_constraint',
      headerName: 'Resource Constraint',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'resource_amount',
      headerName: 'Resource Amount',
      type: 'number',
      flex: 1,
      editable: true,
    },
  ],

  // Bottle-format changeovers: the time, cost and resource draw of moving a
  // line from one format to another. A product's format is DECLARED on the
  // master-data `format` column; this table only prices the transitions
  // between those declared values. See docs/FORMAT_CHANGEOVER.md.
  'format-changeover': (_data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'format_from',
      headerName: 'From Format',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'format_to',
      headerName: 'To Format',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_time',
      headerName: 'Changeover Time (hrs)',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      field: 'changeover_cost',
      headerName: 'Changeover Cost',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      field: 'resource_constraint',
      headerName: 'Resource Constraint',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'resource_amount',
      headerName: 'Resource Amount',
      type: 'number',
      flex: 1,
      editable: true,
    },
  ],

  // One row per resource, one column per hour of the standard week.
  //
  // No `renderCell` on the hour columns, deliberately: 168 of them mounting a
  // component each is what made the per-schedule grid slow, and the same
  // would apply here. A blank hour means "inherit the flat ConstraintAmount",
  // which is why an empty cell is left empty rather than shown as 0.
  'resource-capacity-profile': (
    _data: Record<string, unknown>[]
  ): GridColDef[] => [
    {
      field: 'ResourceConstraint',
      headerName: 'Resource',
      type: 'string',
      width: 160,
      editable: false,
    },
    ...Array.from({ length: 168 }, (_unused, h) => ({
      field: `h${h}`,
      headerName: `${String(h % 24).padStart(2, '0')}:00`,
      description: `Day ${Math.floor(h / 24) + 1}, hour ${h % 24}`,
      type: 'number' as const,
      width: 54,
      editable: true,
      sortable: false,
      filterable: false,
    })),
  ],

  // The vessels themselves. Only `tankId` is required.
  //
  // `maxHoldHours` IS enforced, on a consolidated multi-week run: it sizes the
  // fill into batches, since one batch can serve drains spread over at most
  // `maxHoldHours - maturationHours`. Empty bounds nothing and makes one batch.
  //
  // `capacity` and `UOM` are still parsed and NOT read — a batch is bounded by
  // the kitchen run that fills it — so they are offered rather than hidden,
  // because a column that appears later reads as a new requirement.
  //
  // `tankId` stays editable so a row added here can be named. It is also the
  // merge predicate, so renaming an existing tank inserts a second row rather
  // than moving the first; delete the old one.
  tanks: (_data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'tankId',
      headerName: 'Tank',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'capacity',
      headerName: 'Capacity',
      type: 'number',
      flex: 1,
      editable: true,
      description:
        'How much this vessel holds. Read ONLY when Accumulate is ticked, which it is by default, and then it is the ceiling a top-up must fit under. With Accumulate off the filling is bounded by the kitchen run that makes it and the vessel imposes nothing. Leave empty for no limit — blank means unbounded, not "no top-ups".',
    },
    {
      field: 'allowAccumulation',
      headerName: 'Accumulate',
      type: 'boolean',
      // Coerced: the service stores every column as a string, so a `false`
      // comes back as 'False' and reads truthy. Same rule as Trials' Active.
      valueGetter: (value: unknown) => asBool(value),
      flex: 0.8,
      editable: true,
      description:
        'ON BY DEFAULT. A kitchen run may add to what this vessel already holds, provided it is the SAME product, up to Capacity. Untick it and the vessel takes a new batch only once its contents have been drawn out completely and it has been cleaned - so a filling is one kitchen run, and a quantity larger than (kitchenRunRate x maxRunTime) cannot be made at all and reports as unmet demand. Maturity always runs from the MOST RECENT run into the vessel, so a later top-up delays the whole contents rather than being free; the maximum hold still runs from the first. Splitting ONE filling across several runs happens on a CONSOLIDATED multi-week run.',
    },
    {
      field: 'UOM',
      headerName: 'UOM',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'maxHoldHours',
      headerName: 'Max Hold (h)',
      type: 'number',
      flex: 1,
      editable: true,
      description:
        'The longest this vessel may hold a batch, from the end of the fill that made it to the end of the last drain that empties it. Enforced on a CONSOLIDATED multi-week run: one batch can only serve drains spread over (Max Hold - maturationHours), so a horizon whose demand is spread wider than that is made as several batches, each filled only after the previous one has been drained and the vessel cleaned. Leave empty for no limit, which makes one batch however long it is held. Ignored on a single-week run, where the fill and its drain are in different runs.',
    },
  ],

  // Which product a tank may hold. One row per tank under V1 dedication; the
  // grain is (tank, product), so giving a tank a second product is a second
  // row rather than an edit — which is how V2's shared tanks arrive without a
  // table migration. Both halves key the row.
  'tank-eligibility': (_data: Record<string, unknown>[]): GridColDef[] => [
    {
      field: 'tankId',
      headerName: 'Tank',
      type: 'string',
      flex: 1,
      editable: true,
    },
    {
      field: 'productCode',
      headerName: 'Product Code',
      type: 'string',
      flex: 1,
      editable: true,
    },
  ],

  // Kitchen cycles. A base material is in at most one cycle, so it keys the
  // row; the position orders it within its kitchen's cycle.
  'kitchen-cycles': (_data: Record<string, unknown>[]): GridColDef[] =>
    KITCHEN_CYCLE_COLUMNS,
}

/** Shared with the per-schedule grid (`SchedulerKitchenCycles.muiTable`). */
export const KITCHEN_CYCLE_COLUMNS: GridColDef[] = [
  {
    field: 'kitchen',
    headerName: 'Kitchen',
    type: 'string',
    flex: 1,
    editable: true,
  },
  {
    field: 'baseMaterialCode',
    headerName: 'Base Material Code',
    type: 'string',
    flex: 1,
    editable: true,
  },
  {
    field: 'position',
    headerName: 'Position in Cycle',
    type: 'number',
    flex: 1,
    editable: true,
  },
]
